import { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useToastNotification } from 'hooks/useToastNotification';
import { usePreviewTicketImport } from '../../hooks';
import { useWizard } from '../../store';
import { PreviewRow } from '../../types';
import { handleFileMissingError } from '../../utils';
import {
	Button,
	Card,
	CardDescription,
	CardTitle,
	ErrorPill,
	Footer,
	Row,
	ScrollContainer,
	Select,
	Stat,
	SuccessPill,
	Table,
	WarningPill,
} from '../../TicketImporter.styles';

const PAGE_SIZE = 50;

type RowFilter = 'all' | 'errors' | 'skipped' | 'valid' | 'corrected';

export const PreviewStep = (): JSX.Element => {
	const history = useHistory();
	const { pathname } = useLocation();
	const { state, dispatch } = useWizard();
	const { showNotification, TYPES } = useToastNotification();

	const { mutate, isLoading, data } = usePreviewTicketImport(state.importId as number, {
		onSuccess: (resp) => dispatch({ type: 'setPreview', payload: resp }),
		onError: (err: any) => {
			if (
				handleFileMissingError(err, {
					notify: showNotification,
					notifyType: TYPES,
					reset: () => dispatch({ type: 'reset' }),
					navigate: (p) => history.replace(p),
					basePath: pathname,
				})
			)
				return;
			const msg = err?.response?.data?.error || 'Preview failed';
			showNotification({ type: TYPES.ERROR, message: msg });
		},
	});

	useEffect(() => {
		if (state.importId && !state.preview) {
			mutate();
		}
	}, [state.importId, state.preview, mutate]);

	const preview = data || state.preview;

	const [filter, setFilter] = useState<RowFilter>('all');
	const [page, setPage] = useState(0);

	const filteredRows = useMemo(() => {
		if (!preview) return [];
		return preview.rows.filter((r) => {
			switch (filter) {
				case 'errors':
					return r.errors && r.errors.length > 0;
				case 'skipped':
					return r.already_exists;
				case 'valid':
					return (!r.errors || r.errors.length === 0) && !r.already_exists;
				case 'corrected':
					return r.seat_corrected;
				default:
					return true;
			}
		});
	}, [preview, filter]);

	const pagedRows = useMemo(() => filteredRows.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE), [filteredRows, page]);
	const pageCount = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));

	const renderRowStatus = (r: PreviewRow) => {
		if (r.errors && r.errors.length > 0) {
			return r.errors.map((e, i) => <ErrorPill key={i}>{e}</ErrorPill>);
		}
		if (r.already_exists) {
			return <WarningPill>Skipped — already imported</WarningPill>;
		}
		return <SuccessPill>OK</SuccessPill>;
	};

	return (
		<>
			<Card>
				<CardTitle>6. Preview</CardTitle>
				<CardDescription>
					Every row was resolved against your events and seats.io chart. Nothing has been written to the database yet —
					you can go back and adjust mappings if something looks off.
				</CardDescription>

				{!preview && isLoading && <p>Resolving rows… (this can take a few seconds for large files)</p>}

				{preview && (
					<>
						<Row>
							<Stat>
								<span className="label">Total rows</span>
								<span className="value">{preview.total_rows.toLocaleString()}</span>
							</Stat>
							<Stat>
								<span className="label">Valid</span>
								<span className="value" style={{ color: '#065f46' }}>
									{preview.valid_rows.toLocaleString()}
								</span>
							</Stat>
							<Stat>
								<span className="label">Errors</span>
								<span className="value" style={{ color: '#991b1b' }}>
									{preview.error_rows.toLocaleString()}
								</span>
							</Stat>
							<Stat>
								<span className="label">Skipped (already imported)</span>
								<span className="value" style={{ color: '#92400e' }}>
									{preview.skipped_rows.toLocaleString()}
								</span>
							</Stat>
							<Stat>
								<span className="label">Unique buyers</span>
								<span className="value">{preview.unique_emails.toLocaleString()}</span>
							</Stat>
						</Row>

						<Row>
							<label style={{ fontSize: 13, display: 'flex', gap: 8, alignItems: 'center' }}>
								Show:
								<Select
									value={filter}
									onChange={(e) => {
										setFilter(e.target.value as RowFilter);
										setPage(0);
									}}
								>
									<option value="all">All rows</option>
									<option value="errors">Errors only</option>
									<option value="skipped">Skipped (duplicates) only</option>
									<option value="valid">Valid only</option>
									<option value="corrected">Excel auto-corrected seats</option>
								</Select>
							</label>
							<span style={{ fontSize: 13, color: '#6b7280' }}>
								Showing {pagedRows.length} of {filteredRows.length.toLocaleString()} rows
							</span>
						</Row>

						<ScrollContainer>
							<Table>
								<thead>
									<tr>
										<th>#</th>
										<th>Email</th>
										<th>Event</th>
										<th>Ticket type</th>
										<th>Seat</th>
										<th>Price</th>
										<th>Status</th>
									</tr>
								</thead>
								<tbody>
									{pagedRows.map((r) => (
										<tr key={r.row_index}>
											<td>{r.row_index}</td>
											<td>{r.customer_email || '—'}</td>
											<td>
												{r.event_name || '—'}
												{r.event_id ? <small style={{ color: '#9ca3af' }}> (#{r.event_id})</small> : null}
											</td>
											<td>
												{r.ticket_type_name || '—'}
												{r.ticket_type_id ? <small style={{ color: '#9ca3af' }}> (#{r.ticket_type_id})</small> : null}
											</td>
											<td>
												{r.seat_label || '—'}
												{r.seat_corrected && (
													<>
														{' '}
														<WarningPill>Fixed from {r.seat_original}</WarningPill>
													</>
												)}
											</td>
											<td>${r.price?.toFixed(2) ?? '0.00'}</td>
											<td>{renderRowStatus(r)}</td>
										</tr>
									))}
								</tbody>
							</Table>
						</ScrollContainer>

						<Row>
							<Button variant="secondary" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}>
								← Prev page
							</Button>
							<span style={{ fontSize: 13, color: '#6b7280' }}>
								Page {page + 1} of {pageCount}
							</span>
							<Button
								variant="secondary"
								onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
								disabled={page >= pageCount - 1}
							>
								Next page →
							</Button>
						</Row>
					</>
				)}
			</Card>

			<Footer>
				<Button variant="secondary" onClick={() => history.push(`${pathname}#6`)}>
					← Back
				</Button>
				<div style={{ display: 'flex', gap: 8 }}>
					<Button variant="secondary" onClick={() => mutate()} disabled={isLoading}>
						Re-run preview
					</Button>
					<Button onClick={() => history.push(`${pathname}#8`)} disabled={!preview || preview.valid_rows === 0}>
						Continue to commit ({preview?.valid_rows ?? 0} tickets) →
					</Button>
				</div>
			</Footer>
		</>
	);
};
